import {ThunkDispatch, UnknownAction} from '@reduxjs/toolkit';

import {noop} from 'ramda-adjunct';

import {invalidateOnSuccess, omneticApi} from '@dms/api/base';

import {mergeTelephonyCallState} from './mergeTelephonyCallState';
import {
  AssignTelephonyCallRecordApiArg,
  AttachTelephonyLineNumberApiArg,
  CancelTelephonyCallApiArg,
  ConfigureTelephonyLineRoutingApiArg,
  CreateTelephonyGroupApiArg,
  CreateTelephonyGroupApiResponse,
  CreateTelephonyGroupApiResponseSchema,
  CreateTelephonyLineApiArg,
  CreateTelephonyLineApiResponse,
  CreateTelephonyLineApiResponseSchema,
  DeleteTelephonyGroupApiArg,
  DeleteTelephonyLineApiArg,
  DetachTelephonyLineNumberApiArg,
  GetTelephonyGroupApiArg,
  GetTelephonyGroupApiResponse,
  GetTelephonyGroupsApiResponse,
  GetTelephonyGroupsApiResponseSchema,
  GetTelephonyCallApiArg,
  GetTelephonyCallApiResponse,
  GetTelephonyCallOptionsApiResponse,
  GetTelephonyCallOptionsApiResponseSchema,
  GetTelephonyCurrentCallApiResponse,
  GetTelephonyLineApiArg,
  GetTelephonyLineApiResponse,
  GetTelephonyLinesApiResponse,
  GetTelephonyLinesApiResponseSchema,
  GetTelephonyVoiceTokenApiResponse,
  GetTelephonyVoiceTokenApiResponseSchema,
  GetTwilioAccountApiResponse,
  GetTwilioAccountApiResponseSchema,
  GetTwilioNumbersApiResponse,
  GetTwilioNumbersApiResponseSchema,
  ResetTelephonyLineRoutingApiArg,
  SetTelephonyCallNoteApiArg,
  StartTelephonyCallApiArg,
  StartTelephonyCallApiResponse,
  StartTelephonyCallApiResponseSchema,
  TelephonyCallSchema,
  TelephonyGroupSchema,
  TelephonyLineRecordingConsentApiArg,
  TelephonyLineSchema,
  UnassignTelephonyCallRecordApiArg,
  UpdateTelephonyGroupApiArg,
  UpdateTelephonyLineApiArg,
  GetTelephonyCallDetailApiArg,
  GetTelephonyCallDetailApiResponse,
  TelephonyCallDetailSchema,
  GetTelephonyCallRecordingApiArg,
  GetTelephonyCallRecordingApiResponse,
  GetTelephonyCallRecordingApiResponseSchema,
  TelephonyRecordingSettings,
  TelephonyRecordingSettingsSchema,
  UpdateTelephonyRecordingSettingsApiArg,
} from './types';

/** The threads whose call rows show a call's record links and note. */
const CALL_THREAD_TAGS: ['CustomerMessage', 'ServiceCaseCustomerMessages'] = [
  'CustomerMessage',
  'ServiceCaseCustomerMessages',
];

/** Everything that shows a call's record links or note, the call detail page included. */
const getCallChangeTags = (_result: unknown, queryArg: {callId: string}) => [
  ...CALL_THREAD_TAGS,
  {type: 'TelephonyCallDetail' as const, id: queryArg.callId},
];

export const telephonyApi = omneticApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (build) => ({
    getTwilioAccount: build.query<GetTwilioAccountApiResponse, void>({
      query: () => ({
        url: `/dms/v1/telephony/twilio/account`,
      }),
      extraOptions: {
        responseSchema: GetTwilioAccountApiResponseSchema,
      },
    }),

    getTelephonyLines: build.query<GetTelephonyLinesApiResponse, void>({
      query: () => ({
        url: `/dms/v1/telephony/lines`,
      }),
      providesTags: ['TelephonyLineList'],
      extraOptions: {
        responseSchema: GetTelephonyLinesApiResponseSchema,
      },
    }),

    getTelephonyLine: build.query<GetTelephonyLineApiResponse, GetTelephonyLineApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/telephony/lines/${queryArg.lineId}`,
      }),
      providesTags: (result, error, queryArg) => [{type: 'TelephonyLine', id: queryArg.lineId}],
      extraOptions: {
        responseSchema: TelephonyLineSchema,
      },
    }),

    createTelephonyLine: build.mutation<CreateTelephonyLineApiResponse, CreateTelephonyLineApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/telephony/lines`,
        method: 'POST',
        body: queryArg.body,
      }),
      extraOptions: {
        responseSchema: CreateTelephonyLineApiResponseSchema,
      },
      invalidatesTags: invalidateOnSuccess(['TelephonyLineList']),
    }),

    updateTelephonyLine: build.mutation<void, UpdateTelephonyLineApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/telephony/lines/${queryArg.lineId}`,
        method: 'PUT',
        body: queryArg.body,
      }),
      invalidatesTags: invalidateOnSuccess((result, queryArg: UpdateTelephonyLineApiArg) => [
        {type: 'TelephonyLine', id: queryArg.lineId},
        'TelephonyLineList',
      ]),
    }),

    deleteTelephonyLine: build.mutation<void, DeleteTelephonyLineApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/telephony/lines/${queryArg.lineId}`,
        method: 'DELETE',
      }),
      invalidatesTags: invalidateOnSuccess((result, queryArg: DeleteTelephonyLineApiArg) => [
        {type: 'TelephonyLine', id: queryArg.lineId},
        'TelephonyLineList',
        'TwilioNumberList',
      ]),
    }),

    attachTelephonyLineNumber: build.mutation<void, AttachTelephonyLineNumberApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/telephony/lines/${queryArg.lineId}/twilio-numbers/${queryArg.numberSid}`,
        method: 'PUT',
      }),
      invalidatesTags: invalidateOnSuccess((result, queryArg: AttachTelephonyLineNumberApiArg) => [
        {type: 'TelephonyLine', id: queryArg.lineId},
        'TelephonyLineList',
        'TwilioNumberList',
      ]),
    }),

    detachTelephonyLineNumber: build.mutation<void, DetachTelephonyLineNumberApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/telephony/lines/${queryArg.lineId}/twilio-numbers/${queryArg.numberSid}`,
        method: 'DELETE',
      }),
      invalidatesTags: invalidateOnSuccess((result, queryArg: DetachTelephonyLineNumberApiArg) => [
        {type: 'TelephonyLine', id: queryArg.lineId},
        'TelephonyLineList',
        'TwilioNumberList',
      ]),
    }),

    approveTelephonyLineRecordingConsent: build.mutation<void, TelephonyLineRecordingConsentApiArg>(
      {
        query: (queryArg) => ({
          url: `/dms/v1/telephony/lines/${queryArg.lineId}/recording-consent`,
          method: 'PUT',
        }),
        invalidatesTags: invalidateOnSuccess(
          (result, queryArg: TelephonyLineRecordingConsentApiArg) => [
            {type: 'TelephonyLine', id: queryArg.lineId},
            'TelephonyLineList',
          ]
        ),
      }
    ),

    revokeTelephonyLineRecordingConsent: build.mutation<void, TelephonyLineRecordingConsentApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/telephony/lines/${queryArg.lineId}/recording-consent`,
        method: 'DELETE',
      }),
      invalidatesTags: invalidateOnSuccess(
        (result, queryArg: TelephonyLineRecordingConsentApiArg) => [
          {type: 'TelephonyLine', id: queryArg.lineId},
          'TelephonyLineList',
        ]
      ),
    }),

    configureTelephonyLineRouting: build.mutation<void, ConfigureTelephonyLineRoutingApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/telephony/lines/${queryArg.lineId}/routing`,
        method: 'PUT',
        body: queryArg.body,
      }),
      invalidatesTags: invalidateOnSuccess(
        (result, queryArg: ConfigureTelephonyLineRoutingApiArg) => [
          {type: 'TelephonyLine', id: queryArg.lineId},
          'TelephonyLineList',
        ]
      ),
    }),

    resetTelephonyLineRouting: build.mutation<void, ResetTelephonyLineRoutingApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/telephony/lines/${queryArg.lineId}/routing`,
        method: 'DELETE',
      }),
      invalidatesTags: invalidateOnSuccess((result, queryArg: ResetTelephonyLineRoutingApiArg) => [
        {type: 'TelephonyLine', id: queryArg.lineId},
        'TelephonyLineList',
      ]),
    }),

    getTelephonyGroups: build.query<GetTelephonyGroupsApiResponse, void>({
      query: () => ({
        url: `/dms/v1/telephony/groups`,
      }),
      providesTags: ['TelephonyGroupList'],
      extraOptions: {
        responseSchema: GetTelephonyGroupsApiResponseSchema,
      },
    }),

    getTelephonyGroup: build.query<GetTelephonyGroupApiResponse, GetTelephonyGroupApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/telephony/groups/${queryArg.groupId}`,
      }),
      providesTags: (result, error, queryArg) => [{type: 'TelephonyGroup', id: queryArg.groupId}],
      extraOptions: {
        responseSchema: TelephonyGroupSchema,
      },
    }),

    createTelephonyGroup: build.mutation<
      CreateTelephonyGroupApiResponse,
      CreateTelephonyGroupApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/telephony/groups`,
        method: 'POST',
        body: queryArg.body,
      }),
      extraOptions: {
        responseSchema: CreateTelephonyGroupApiResponseSchema,
      },
      invalidatesTags: invalidateOnSuccess(['TelephonyGroupList']),
    }),

    updateTelephonyGroup: build.mutation<void, UpdateTelephonyGroupApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/telephony/groups/${queryArg.groupId}`,
        method: 'PUT',
        body: queryArg.body,
      }),
      invalidatesTags: invalidateOnSuccess((result, queryArg: UpdateTelephonyGroupApiArg) => [
        {type: 'TelephonyGroup', id: queryArg.groupId},
        'TelephonyGroupList',
      ]),
    }),

    deleteTelephonyGroup: build.mutation<void, DeleteTelephonyGroupApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/telephony/groups/${queryArg.groupId}`,
        method: 'DELETE',
      }),
      invalidatesTags: invalidateOnSuccess((result, queryArg: DeleteTelephonyGroupApiArg) => [
        {type: 'TelephonyGroup', id: queryArg.groupId},
        'TelephonyGroupList',
      ]),
    }),

    getTwilioNumbers: build.query<GetTwilioNumbersApiResponse, void>({
      query: () => ({
        url: `/dms/v1/telephony/twilio/numbers`,
      }),
      providesTags: ['TwilioNumberList'],
      extraOptions: {
        responseSchema: GetTwilioNumbersApiResponseSchema,
      },
    }),

    getTelephonyCallOptions: build.query<GetTelephonyCallOptionsApiResponse, void>({
      query: () => ({
        url: `/dms/v1/telephony/call-options`,
      }),
      extraOptions: {
        responseSchema: GetTelephonyCallOptionsApiResponseSchema,
      },
    }),

    startTelephonyCall: build.mutation<StartTelephonyCallApiResponse, StartTelephonyCallApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/telephony/calls`,
        method: 'POST',
        body: queryArg.body,
      }),
      extraOptions: {
        responseSchema: StartTelephonyCallApiResponseSchema,
      },
    }),

    /**
     * The viewer's newest live call of either direction, or `null` (HTTP 204) when there is none.
     * Live state then arrives through the `telephony-{outbound,inbound}_call_state_changed`
     * background notifications, which patch this cache entry; the push channel has no replay, so
     * this is the source after a reload and the way an idle tab discovers a call started elsewhere.
     */
    getTelephonyCurrentCall: build.query<GetTelephonyCurrentCallApiResponse, void>({
      query: () => ({
        url: `/dms/v1/telephony/calls/current`,
      }),
      /**
       * HTTP 204 and any shape the top bar does not know (prod skips `responseSchema`) read as no
       * call, so an unexpected response hides the pill instead of breaking the layout.
       */
      transformResponse: (response: unknown) => {
        const parsed = TelephonyCallSchema.safeParse(response);

        return parsed.success ? parsed.data : null;
      },
      /**
       * Pushes may have moved the cache further while this request was in flight; an older
       * response must not drag the state back or hide a call that started meanwhile.
       */
      merge: (currentCacheData, responseData) =>
        mergeTelephonyCallState(currentCacheData, responseData),
      extraOptions: {
        responseSchema: TelephonyCallSchema.nullable(),
      },
    }),

    getTelephonyCall: build.query<GetTelephonyCallApiResponse, GetTelephonyCallApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/telephony/calls/${queryArg.callId}`,
      }),
      /** Parsed, so `records` and `note` get their defaults when the backend leaves them out. */
      transformResponse: (response: unknown) => TelephonyCallSchema.parse(response),
      extraOptions: {
        responseSchema: TelephonyCallSchema,
      },
    }),

    /**
     * Ends a browser call whose tab never reached Twilio (microphone denied, connection failed).
     * Idempotent: once Twilio has the call, or it already ended, the backend changes nothing.
     */
    cancelTelephonyCall: build.mutation<void, CancelTelephonyCallApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/telephony/calls/${queryArg.callId}/cancel`,
        method: 'POST',
      }),
    }),

    /**
     * Neither pairing call pushes a state change, so readers of `records` refetch the call. A
     * stored call's pairings are the links of its customer thread row, so the thread is refetched.
     */
    assignTelephonyCallRecord: build.mutation<void, AssignTelephonyCallRecordApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/telephony/calls/${queryArg.callId}/records`,
        method: 'PUT',
        body: queryArg.body,
      }),
      invalidatesTags: invalidateOnSuccess(getCallChangeTags),
      onQueryStarted: (queryArg, {dispatch, queryFulfilled}) =>
        refetchCallAfterChange(queryArg.callId, dispatch, queryFulfilled, {isPairing: true}),
    }),

    unassignTelephonyCallRecord: build.mutation<void, UnassignTelephonyCallRecordApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/telephony/calls/${queryArg.callId}/records/${queryArg.resourceId}/${queryArg.recordId}`,
        method: 'DELETE',
      }),
      invalidatesTags: invalidateOnSuccess(getCallChangeTags),
      onQueryStarted: (queryArg, {dispatch, queryFulfilled}) =>
        refetchCallAfterChange(queryArg.callId, dispatch, queryFulfilled, {isPairing: true}),
    }),

    /** Live or ended; pushes nothing, so readers of `note` refetch the call. */
    setTelephonyCallNote: build.mutation<void, SetTelephonyCallNoteApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/telephony/calls/${queryArg.callId}/note`,
        method: 'PUT',
        body: queryArg.body,
      }),
      invalidatesTags: invalidateOnSuccess(getCallChangeTags),
      onQueryStarted: (queryArg, {dispatch, queryFulfilled}) =>
        refetchCallAfterChange(queryArg.callId, dispatch, queryFulfilled, {isPairing: false}),
    }),

    /** The stored call for the call detail page; 404 while the call is live or when it was never stored. */
    getTelephonyCallDetail: build.query<
      GetTelephonyCallDetailApiResponse,
      GetTelephonyCallDetailApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/telephony/calls/${queryArg.callId}/detail`,
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'TelephonyCallDetail', id: queryArg.callId},
      ],
      extraOptions: {
        responseSchema: TelephonyCallDetailSchema,
      },
    }),

    /**
     * A signed link to the stored recording, asked for when the player mounts on Play; it expires,
     * so nothing is kept once the player is gone.
     */
    getTelephonyCallRecording: build.query<
      GetTelephonyCallRecordingApiResponse,
      GetTelephonyCallRecordingApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/telephony/calls/${queryArg.callId}/recording`,
      }),
      keepUnusedDataFor: 0,
      extraOptions: {
        responseSchema: GetTelephonyCallRecordingApiResponseSchema,
      },
    }),

    getTelephonyRecordingSettings: build.query<TelephonyRecordingSettings, void>({
      query: () => ({
        url: `/dms/v1/telephony/recording-settings`,
      }),
      providesTags: ['TelephonyRecordingSettings'],
      extraOptions: {
        responseSchema: TelephonyRecordingSettingsSchema,
      },
    }),

    /** Answers 204 with no body; the settings are read again for the new who / when. */
    updateTelephonyRecordingSettings: build.mutation<void, UpdateTelephonyRecordingSettingsApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/telephony/recording-settings`,
        method: 'PUT',
        body: queryArg.body,
      }),
      invalidatesTags: invalidateOnSuccess(['TelephonyRecordingSettings']),
    }),

    /** A fresh Voice SDK token on every call; tokens expire, so nothing is kept in the cache. */
    getTelephonyVoiceToken: build.query<GetTelephonyVoiceTokenApiResponse, void>({
      query: () => ({
        url: `/dms/v1/telephony/voice-token`,
      }),
      keepUnusedDataFor: 0,
      extraOptions: {
        responseSchema: GetTelephonyVoiceTokenApiResponseSchema,
      },
    }),
  }),
});

/**
 * After a pairing or note change, which push nothing: the call panel reads the call itself and that
 * endpoint has no cache tag (the call detail page's has, and the mutations invalidate it), so read
 * it again, and only then let the mutation's callers go on. A pairing can give the live call its
 * customer, so the current call is read again too.
 */
async function refetchCallAfterChange(
  callId: string,
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>,
  queryFulfilled: Promise<unknown>,
  options: {isPairing: boolean}
) {
  try {
    await queryFulfilled;
  } catch {
    return;
  }

  await Promise.all([
    dispatch(
      telephonyApi.endpoints.getTelephonyCall.initiate(
        {callId},
        {forceRefetch: true, subscribe: false}
      )
    )
      .unwrap()
      .catch(noop),
    options.isPairing
      ? dispatch(
          telephonyApi.endpoints.getTelephonyCurrentCall.initiate(undefined, {
            forceRefetch: true,
            subscribe: false,
          })
        )
          .unwrap()
          .catch(noop)
      : undefined,
  ]);
}

export const {
  useGetTwilioAccountQuery,
  useGetTelephonyLinesQuery,
  useGetTelephonyLineQuery,
  useCreateTelephonyLineMutation,
  useUpdateTelephonyLineMutation,
  useDeleteTelephonyLineMutation,
  useAttachTelephonyLineNumberMutation,
  useDetachTelephonyLineNumberMutation,
  useApproveTelephonyLineRecordingConsentMutation,
  useRevokeTelephonyLineRecordingConsentMutation,
  useConfigureTelephonyLineRoutingMutation,
  useResetTelephonyLineRoutingMutation,
  useGetTelephonyGroupsQuery,
  useGetTelephonyGroupQuery,
  useCreateTelephonyGroupMutation,
  useUpdateTelephonyGroupMutation,
  useDeleteTelephonyGroupMutation,
  useGetTwilioNumbersQuery,
  useGetTelephonyCallOptionsQuery,
  useStartTelephonyCallMutation,
  useGetTelephonyCurrentCallQuery,
  useLazyGetTelephonyCurrentCallQuery,
  useGetTelephonyCallQuery,
  useGetTelephonyCallDetailQuery,
  useLazyGetTelephonyCallQuery,
  useCancelTelephonyCallMutation,
  useAssignTelephonyCallRecordMutation,
  useUnassignTelephonyCallRecordMutation,
  useSetTelephonyCallNoteMutation,
  useLazyGetTelephonyVoiceTokenQuery,
  useGetTelephonyCallRecordingQuery,
  useGetTelephonyRecordingSettingsQuery,
  useUpdateTelephonyRecordingSettingsMutation,
} = telephonyApi;
